import { useState } from 'react';

import type { RescanCloudeqEntityResponse } from '@clm/shared-types';

import { rescanCloudeqEntity } from '../../api/cloudeq-entity';
import { PIPELINE_FORBIDDEN_COPY } from '../pipeline-status-chips';
import { runProblemHead, toRunProblem, type RunProblem } from './shared';

/**
 * The cloudEQ entity **Rescan** — CLM-91 (D-91-7).
 *
 * Admin only (the parent hides it for an identity known not to be an admin;
 * the API's `contract:set_cloudeq_entity` gate decides). Two steps, the same
 * request twice: **Dry run** reports what the in-tenant scan would change over
 * every converted contract in scope — how many move to which entity, which are
 * ambiguous, how many admin values it would leave alone — and writes nothing;
 * **Apply** issues the same request without `dry_run` and writes one audit row
 * per contract whose value changes, none for the rest. No document is
 * downloaded, converted or sent to a model: the text is already in Postgres.
 */

const LIMIT = 500;

export function RescanCloudeqEntityCard() {
  const [busy, setBusy] = useState<'dry' | 'apply' | null>(null);
  const [result, setResult] = useState<RescanCloudeqEntityResponse | null>(null);
  const [problem, setProblem] = useState<RunProblem | null>(null);

  const run = async (dryRun: boolean) => {
    setBusy(dryRun ? 'dry' : 'apply');
    setProblem(null);
    try {
      setResult(await rescanCloudeqEntity({ limit: LIMIT, dry_run: dryRun }));
    } catch (error) {
      setProblem(toRunProblem(error));
    } finally {
      setBusy(null);
    }
  };

  const entityRows = result === null ? [] : Object.entries(result.by_entity).toSorted(([a], [b]) => a.localeCompare(b));

  return (
    <div className="card" role="group" aria-label="cloudEQ entity rescan">
      <h4>cloudEQ entity rescan</h4>
      <p className="mut">
        Re-runs the deterministic in-tenant scan of each converted contract&apos;s text against the cloudEQ entity registry.
        Nothing is downloaded, converted or sent to a model. Admin-set values are never touched. Dry run first; Apply writes
        one audit row per changed contract.
      </p>
      <div className="vacts">
        <button type="button" className="btn" disabled={busy !== null} onClick={() => void run(true)}>
          {busy === 'dry' ? 'Scanning…' : 'Dry run'}
        </button>
        <button
          type="button"
          className="btn pri"
          disabled={busy !== null || result === null || result.dry_run === false}
          onClick={() => void run(false)}
        >
          {busy === 'apply' ? 'Applying…' : 'Apply'}
        </button>
      </div>
      {result === null ? null : (
        <div role="status" data-testid="rescan-result">
          <p>
            <strong>{result.dry_run ? 'Dry run' : 'Applied'}:</strong> {result.scanned} scanned · {result.changed}{' '}
            {result.dry_run ? 'would change' : 'changed'} · {result.unchanged} unchanged · {result.skipped_admin} admin-set skipped ·
            registry of {result.registry_size}
            {result.dry_run ? '' : ' — one audit row per changed contract'}
          </p>
          {entityRows.length === 0 ? null : (
            <ul className="tags-ro" aria-label="Contracts by entity after the scan">
              {entityRows.map(([key, n]) => (
                <li key={key} className="tag">
                  {key}: {n}
                </li>
              ))}
            </ul>
          )}
          {result.ambiguous.length === 0 ? null : (
            <>
              <p className="mut">Ambiguous — more than one entity named; left Unassigned, set them on the Contract record:</p>
              <ul aria-label="Ambiguous contracts">
                {result.ambiguous.map((row) => (
                  <li key={row.contract_key}>
                    {row.contract_key} — {row.candidates.join(', ')}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
      {problem === null ? null : (
        <div className="banner up" role="status">
          <p className="notice-head">
            <strong>{runProblemHead(problem, PIPELINE_FORBIDDEN_COPY)}</strong>
          </p>
          {problem.field_errors.map((entry) => (
            <p key={entry.path} className="mut">
              {entry.message}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
